import React from "react"
import PropTypes from "prop-types"
import classNames from "classnames"
import { useMantineTheme, createStyles } from "@mantine/core"

// style for all Square Inlines
import "app/static/stylus/SquareInlinePresentation.styl"

import { CardSquare } from "QuorumDesign"

import { CardSquare as NewCardSquare } from "@/components/CardSquare"

// components for all Inlines
import Dropdown from "app/static/frontend/search-new/components/Inline/components/DropdownOptions"

// components for officials, executives, and staffers
import AlignmentAdder from "app/static/frontend/containers/AlignmentAdder"
import ListAdder from "app/static/frontend/projects-new/new-list/NewListAdder"
import IssueAdder from "app/static/frontend/components/IssueAdder"

// Inline helper functions
import {
    handleClickOutside,
    onError,
    renderText,
    segueToProfile,
} from "app/static/frontend/search-new/components/Inline/shared/functions"

import { IconWithTooltip } from "@/components/IconWithTooltip"
import { isFeatureEnabled } from "shared/featureflags/helperFunctions"
import { Badge } from "@/components"
import classes from "app/static/frontend/search-new/components/Inline/styles/styles.module.css"

const SquareInlinePresentation = (props) => {
    const theme = useMantineTheme()

    /** render functions */

    const renderSecondLine = () => (
        <div
            className={classNames(
                "pcard-title",
                { hoverscroll: !props.expandTitle },
                { "pcard-title-expanded": props.expandTitle },
            )}
        >
            {renderText({
                lineData: props.secondLineData,
                isHtml: props.secondLineIsHtml,
                inlineId: props.inlineId,
                isSearchify: props.isHighlightable,
            })}
        </div>
    )

    const contentLines = "hoverscroll pcard-content-line"

    const renderThirdLine = () => (
        <div className={contentLines}>
            {renderText({
                lineData: props.thirdLineData,
                isHtml: props.thirdLineIsHtml,
                inlineId: props.inlineId,
                isSearchify: props.isHighlightable,
            })}
        </div>
    )

    const renderFourthLine = () => (
        <div className={contentLines}>
            {renderText({
                lineData: props.fourthLineData,
                isHtml: props.fourthLineIsHtml,
                inlineId: props.inlineId,
                isSearchify: props.isHighlightable,
            })}
        </div>
    )

    const renderFifthLine = () => (
        <div className={contentLines}>
            {renderText({
                lineData: props.fifthLineData,
                isHtml: props.fifthLineIsHtml,
                inlineId: props.inlineId,
                isSearchify: props.isHighlightable,
            })}
        </div>
    )

    const renderSixthLine = () => (
        <div className={contentLines}>
            {renderText({
                lineData: props.sixthLineData,
                isHtml: props.sixthLineIsHtml,
                inlineId: props.inlineId,
                isSearchify: props.isHighlightable,
            })}
        </div>
    )

    const renderLineText = (lineObject) => {
        if (!lineObject) {
            return undefined
        } else if (lineObject && !lineObject.isHtml) {
            return lineObject
        }

        return {
            ...lineObject,
            value: renderText({
                lineData: lineObject.value,
                isHtml: true,
                inlineId: props.inlineId,
                isSearchify: props.isHighlightable,
            }),
            valueTooltip: renderText({
                lineData: lineObject.valueTooltip,
                isHtml: true,
                inlineId: props.inlineId,
                isSearchify: props.isHighlightable,
            }),
        }
    }

    if (!props.appearanceValues.get("isHidden")) {
        const renderAdder = () => {
            if (props.appearanceValues.get("listDropDownEnabled")) {
                return (
                    <ListAdder
                        inline
                        showHeader
                        datumId={props.inlineId}
                        handleClickOutside={() =>
                            handleClickOutside(props.updateInlineAppearance, props.uniqueFlvId, props.inlineId)
                        }
                        model={props.listModel ? props.listModel : props.model}
                    />
                )
            } else if (props.appearanceValues.get("issueDropDownEnabled")) {
                return (
                    <IssueAdder
                        dataType={DjangIO.app.models.QuorumDataType.by_value(props.model)}
                        inline
                        profileId={props.inlineId}
                        handleClickOutside={() =>
                            handleClickOutside(props.updateInlineAppearance, props.uniqueFlvId, props.inlineId)
                        }
                    />
                )
            }
            return undefined
        }

        const getAdderType = () => {
            if (props.appearanceValues.get("listDropDownEnabled")) {
                return "Lists"
            } else if (props.appearanceValues.get("issueDropDownEnabled")) {
                return "Issues"
            }

            return undefined
        }

        return isFeatureEnabled("ff_new_card_square") ? (
            <NewCardSquare
                // Profile Picture props
                borderColor={props.imageBorderColor?.borderColor}
                imageUrl={props.imagePath}
                imageUrlOnError={onError}
                // Content props
                contentLines={[
                    ...(props.secondLine ? [renderLineText(props.secondLine)] : []),
                    ...(props.thirdLine ? [renderLineText(props.thirdLine)] : []),
                    ...(props.fourthLine ? [renderLineText(props.fourthLine)] : []),
                    ...(props.fifthLine ? [renderLineText(props.fifthLine)] : []),
                    ...(props.sixthLine ? [renderLineText(props.sixthLine)] : []),
                ]}
                name={props.name}
                onClick={(event) => segueToProfile(props.onPress, event)}
                publicOrgDescriptor={props.publicOrgDescriptor}
                // Footer props
                adderComponent={renderAdder()}
                adderType={getAdderType()}
                footerButtons={props.options?.filter((option) => !option.isDropdown)}
                footerSelects={props.options?.filter((option) => option.isDropdown)}
            />
        ) : (
            <CardSquare
                AdderComponent={renderAdder()}
                adderType={getAdderType()}
                // S.CardSquare
                cardSquareClassName="shared-inline pcard-inline"
                cardSquareDataCy={`shared-inline-${props.index}`}
                // S.CardSquareInner
                cardSquareInnerClassName={classNames(
                    "pcard-body inline cardholder",
                    props.personType,
                    { "is-searchify": props.isHighlightable },
                    {
                        "is-searchify-selected":
                            props.isSearchifySelected || (props.isBulkActions && props.isBulkActionsSelected),
                    },
                )}
                cardSquareInnerDataCy="inline-card"
                cardSquareInnerOnClick={(event) => segueToProfile(props.onPress, event)}
                // S.CardSquareInnerAnchor
                cardSquareInnerAnchorHref={props.isHighlightable ? `#${props.inlineId}` : props.href}
                // Icon
                iconContainerClassName={classNames("person-top-left", { "fix-image": props.abbrev })}
                imagePath={props.imagePath}
                imagePathClassName={classNames("person-profile-img", props.imageBorderRule)}
                imagePathOnError={onError}
                imageStyle={props.imageStyle}
                stateIcon={props.stateIcon}
                abbrev={props.abbrev}
                iconName={props.iconName}
                // S.CardSquareContent
                cardSquareContentClassName="pcard-content"
                cardSquareContentChildren={
                    <React.Fragment>
                        {!props.secondLine && props.secondLineData && renderSecondLine()}
                        {!props.thirdLine && props.thirdLineData && renderThirdLine()}
                        {!props.fourthLine && props.fourthLineData && renderFourthLine()}
                        {!props.fifthLine && props.fifthLineData && renderFifthLine()}
                        {!props.sixthLine && props.sixthLineData && renderSixthLine()}
                    </React.Fragment>
                }
                firstLineIcon={
                    props.isCustom && (
                        <IconWithTooltip
                            icon="building"
                            iconColor={theme.colors.brand[4]}
                            iconStyle={{ marginLeft: "3px", marginTop: "3px" }}
                            label="Added by your organization"
                            tooltipPosition="right"
                            withArrow
                        />
                    )
                }
                firstLineText={
                    props.firstLineData &&
                    renderText({
                        lineData: props.firstLineData,
                        isHtml: props.firstLineIsHtml,
                        inlineId: props.inlineId,
                        isSearchify: props.isHighlightable,
                    })
                }
                secondLine={props.secondLine && renderLineText(props.secondLine)}
                thirdLine={props.thirdLine && renderLineText(props.thirdLine)}
                fourthLine={props.fourthLine && renderLineText(props.fourthLine)}
                fifthLine={props.fifthLine && renderLineText(props.fifthLine)}
                sixthLine={props.sixthLine && renderLineText(props.sixthLine)}
                isHighlightable={props.isHighlightable}
                isSelected={props.isSearchifySelected || (props.isBulkActions && props.isBulkActionsSelected)}
                pacBadge={
                    props.isPacContact && (
                        <Badge className={classes.pacContactBadge} variant="light" radius="xl">
                            PAC contact
                        </Badge>
                    )
                }
                pacData={
                    props.isPacContact && {
                        email: props.pacData?.email && renderLineText(props.pacData?.email),
                        phone: props.pacData?.phone && renderLineText(props.pacData?.phone),
                        address: props.pacData?.address && renderLineText(props.pacData?.address),
                    }
                }
                options={props.options}
            >
                {props.appearanceValues.get("alignmentDropDownEnabled") && (
                    <Dropdown>
                        <AlignmentAdder
                            dataType={DjangIO.app.models.QuorumDataType.by_value(props.model)}
                            inline
                            profileId={props.inlineId}
                            handleClickOutside={() =>
                                handleClickOutside(props.updateInlineAppearance, props.uniqueFlvId, props.inlineId)
                            }
                        />
                    </Dropdown>
                )}
            </CardSquare>
        )
    }

    // if we have hidden or deleted the Inline, we want to prevent react from re-rendering it to the page
    // https://stackoverflow.com/questions/42083181/is-it-possible-to-return-empty-in-react-render-function
    return null
}

SquareInlinePresentation.propTypes = {
    abbrev: PropTypes.string,
    appearanceValues: PropTypes.object,
    expandTitle: PropTypes.bool,
    href: PropTypes.string,
    imagePath: PropTypes.string,
    imageBorderRule: PropTypes.string,
    imageStyle: PropTypes.object,
    iconName: PropTypes.string,
    index: PropTypes.number,
    inlineId: PropTypes.number,
    isPerson: PropTypes.bool,
    isCustom: PropTypes.bool,
    stateIcon: PropTypes.string,
    firstLineData: PropTypes.string,
    secondLineData: PropTypes.string,
    thirdLineData: PropTypes.string,
    fourthLineData: PropTypes.string,
    fifthLineData: PropTypes.string,
    sixthLineData: PropTypes.string,
    firstLineIsHtml: PropTypes.bool,
    secondLineIsHtml: PropTypes.bool,
    thirdLineIsHtml: PropTypes.bool,
    fourthLineIsHtml: PropTypes.bool,
    fifthLineIsHtml: PropTypes.bool,
    sixthLineIsHtml: PropTypes.bool,
    model: PropTypes.number,
    name: PropTypes.string,
    listModel: PropTypes.number,
    onPress: PropTypes.func,
    options: PropTypes.arrayOf(PropTypes.object),
    personType: PropTypes.string,
    publicOrgDescriptor: PropTypes.string,
    requestChanges: PropTypes.func,
    isBulkActions: PropTypes.bool,
    isBulkActionsSelected: PropTypes.bool,
    isHighlightable: PropTypes.bool,
    isSearchify: PropTypes.bool,
    isSearchifySelected: PropTypes.bool,
    disableFeedback: PropTypes.bool,
    uniqueFlvId: PropTypes.string,
    updateInlineAppearance: PropTypes.func,
}

SquareInlinePresentation.defaultProps = {
    disableFeedback: false,
}

export default SquareInlinePresentation
